/**
 * K6: the far backdrop of an open-world region. Baked ONCE per region (`setRegionBackdrop`) into a low-res canvas
 * (one pixel = `S` tiles) of the region's ground colours (climate base, the placed zones' tint, and far landmarks:
 * snow ridges to the north, a desert tint to the east, forest and water bands) from region data, then drawn as ONE
 * affine drawImage per frame under the streamed ground (ground layer, between the sky 110 and the ground 120), so
 * chunks beyond the stream radius show colour instead of nothing and the loaded edge sits on matching tones.
 * Inactive until a clone calls `setRegionBackdrop`; the main game never does. Pixel rules: main-palette tones,
 * hard 1-px steps (smoothing off), no glow, no gradients (dither bands).
 */
import { PLACES, REGIONS, type Climate, type RegionId } from '../../../../../packages/shared/src/data/regions';
import { registerRenderPass } from './registry';

/** Tiles per backdrop pixel. */
const S = 8;

const BASE: Record<Climate, string[]> = {
  plains: ['#8aa356', '#7d9850'],
  snow: ['#dfe8ea', '#cbd8dc'],
  delta: ['#6f9a6a', '#5f8c66'],
  desert: ['#d8b575', '#cca868'],
  forest: ['#4f7a43', '#46703c'],
};
const PLACE_TINT = '#a89f84'; // paved / built-up ground under a placed zone
const RIDGE = ['#8d9aa8', '#6b7889', '#4a566a']; // far mountains: lit, mid, shade
const SNOWCAP = '#f1f5f6';

/** What lies beyond each region edge (x east, y south): a far landmark band. */
const BEYOND: Record<RegionId, Partial<Record<'n' | 'e' | 's' | 'w', 'ridge' | 'desert' | 'forest' | 'water' | 'plains'>>> = {
  centre: { n: 'ridge', e: 'desert', s: 'forest', w: 'water' },
  north: { s: 'plains', e: 'ridge', w: 'ridge', n: 'ridge' },
  west: { e: 'plains', n: 'water', s: 'water', w: 'water' },
  east: { w: 'plains', n: 'desert', s: 'desert', e: 'desert' },
  south: { n: 'plains', e: 'forest', w: 'forest', s: 'forest' },
};

const hash = (x: number, y: number): number => {
  let h = (Math.imul(x, 374761393) + Math.imul(y, 668265263)) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
};

interface Backdrop {
  readonly img: HTMLCanvasElement;
  readonly w: number; // region tiles
  readonly h: number;
}
let current: Backdrop | null = null;

/** Bakes the backdrop of `region` (once; call again only when the region changes). `null` switches it off. */
export function setRegionBackdrop(region: RegionId | null): void {
  if (!region) { current = null; return; }
  const def = REGIONS[region];
  const W = Math.ceil(def.w / S), H = Math.ceil(def.h / S);
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const g = c.getContext('2d')!;
  const base = BASE[def.climate];
  for (let y = 0; y < H; y++)
    for (let x = 0; x < W; x++) {
      g.fillStyle = base[hash(x >> 1, y >> 1) < 0.35 ? 1 : 0]!;
      g.fillRect(x, y, 1, 1);
    }
  g.fillStyle = PLACE_TINT;
  for (const p of PLACES) if (p.region === region) g.fillRect(Math.floor(p.rect.x / S), Math.floor(p.rect.y / S), Math.ceil(p.rect.w / S), Math.ceil(p.rect.h / S));
  // Far landmark bands along each edge (depth 14 px = 112 tiles), ordered dither so the edge steps, never blurs.
  const D = 14;
  const band = (edge: 'n' | 'e' | 's' | 'w', kind: string): void => {
    for (let t = 0; t < (edge === 'n' || edge === 's' ? W : H); t++) {
      const ridgeH = Math.round(D * (0.45 + 0.55 * hash(t >> 2, edge.charCodeAt(0))));
      for (let d = 0; d < D; d++) {
        let col: string | null;
        if (kind === 'ridge') col = d < ridgeH ? (d < 2 ? SNOWCAP : RIDGE[d < ridgeH * 0.5 ? 0 : (t + d) & 1 ? 1 : 2]!) : null;
        else if (kind === 'water') col = d < D * 0.5 ? '#6aa7b8' : ((t + d) & 1) === 0 && d < D * 0.8 ? '#9fd0d6' : null;
        else {
          const k = kind === 'desert' ? BASE.desert : kind === 'forest' ? BASE.forest : BASE.plains;
          col = d < D * 0.6 || ((t + d) & 1) === 0 ? k[(t >> 1) & 1]! : null;
        }
        if (!col) continue;
        const [x, y] = edge === 'n' ? [t, d] : edge === 's' ? [t, H - 1 - d] : edge === 'w' ? [d, t] : [W - 1 - d, t];
        g.fillStyle = col;
        g.fillRect(x, y, 1, 1);
      }
    }
  };
  for (const [e, k] of Object.entries(BEYOND[region])) if (k) band(e as 'n' | 'e' | 's' | 'w', k);
  current = { img: c, w: def.w, h: def.h };
}

registerRenderPass({
  id: 'regionBackdrop',
  layer: 'ground',
  order: 118, // sky 110 < this < ground 120 (grepped: no other ground pass at 118)
  draw(f) {
    const b = current;
    if (!b || !f.outdoors) return;
    // The region square is a parallelogram on screen: three projected corners give the affine map for one drawImage.
    const [ax, ay] = f.iso.proj(0, 0);
    const [bx, by] = f.iso.proj(b.w, 0);
    const [cx, cy] = f.iso.proj(0, b.h);
    const g = f.g;
    g.save();
    g.imageSmoothingEnabled = false;
    g.setTransform((bx - ax) / b.img.width, (by - ay) / b.img.width, (cx - ax) / b.img.height, (cy - ay) / b.img.height, ax + f.ox, ay + f.oy);
    g.drawImage(b.img, 0, 0);
    g.restore();
  },
});
